import React from "react";
import { Box, Paper, Typography, Grid } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { dash } from "./dashboardTokens";

const COLORS = {
  scheduled: "#d97706",
  sent: "#059669",
  failed: "#dc2626",
  other: "#64748b",
};

function MiniStat({ label, value, color }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.75,
        borderRadius: 1.5,
        border: `1px solid ${dash.border}`,
        bgcolor: alpha(color || "#64748b", 0.06),
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, color: dash.textMuted, letterSpacing: "0.05em", fontSize: 10 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: 22, mt: 0.5, fontFeatureSettings: '"tnum"', color: "text.primary" }}>
        {value ?? 0}
      </Typography>
    </Paper>
  );
}

/**
 * Broadcast summary + proportional insight bar (no per-campaign table).
 */
export default function BroadcastInsightsCard({ insights, periodLabel }) {
  const s = insights || {};
  const total = s.total_created ?? 0;
  const scheduled = s.scheduled ?? 0;
  const sent = s.sent ?? 0;
  const failed = s.failed_campaigns ?? 0;
  const other = Math.max(0, total - scheduled - sent - failed);
  const recipientFailures = s.recipient_failures ?? 0;

  const pct = (n) => (total > 0 ? (n / total) * 100 : 0);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        border: `1px solid ${dash.border}`,
        bgcolor: dash.surface,
        boxShadow: dash.shadowSm,
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 14, color: dash.textMuted, mb: 2 }}>
        Campaign outcomes · {periodLabel || "selected range"}
      </Typography>

      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2.5 }}>
        <Grid item xs={6} sm={3}>
          <MiniStat label="Created" value={total} color="#2563eb" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MiniStat label="Scheduled" value={scheduled} color={COLORS.scheduled} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MiniStat label="Sent" value={sent} color={COLORS.sent} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MiniStat label="Failed" value={failed} color={COLORS.failed} />
        </Grid>
      </Grid>

      {total === 0 ? (
        <Box
          sx={{
            py: 3,
            textAlign: "center",
            borderRadius: 1.5,
            bgcolor: alpha("#64748b", 0.06),
            border: `1px dashed ${dash.border}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No broadcasts in this period.
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="caption" sx={{ fontWeight: 600, color: dash.textMuted, display: "block", mb: 1 }}>
            Status mix
          </Typography>
          <Box
            sx={{
              display: "flex",
              height: 14,
              borderRadius: 999,
              overflow: "hidden",
              bgcolor: alpha("#e2e8f0", 0.9),
            }}
          >
            {pct(scheduled) > 0 && (
              <Box
                title={`Scheduled: ${scheduled}`}
                sx={{ width: `${pct(scheduled)}%`, bgcolor: COLORS.scheduled, minWidth: pct(scheduled) > 0 ? 4 : 0, transition: "width 0.4s ease" }}
              />
            )}
            {pct(sent) > 0 && (
              <Box
                title={`Sent: ${sent}`}
                sx={{ width: `${pct(sent)}%`, bgcolor: COLORS.sent, minWidth: pct(sent) > 0 ? 4 : 0, transition: "width 0.4s ease" }}
              />
            )}
            {pct(failed) > 0 && (
              <Box
                title={`Failed: ${failed}`}
                sx={{ width: `${pct(failed)}%`, bgcolor: COLORS.failed, minWidth: pct(failed) > 0 ? 4 : 0, transition: "width 0.4s ease" }}
              />
            )}
            {pct(other) > 0 && (
              <Box
                title={`Other: ${other}`}
                sx={{ width: `${pct(other)}%`, bgcolor: COLORS.other, minWidth: pct(other) > 0 ? 4 : 0, transition: "width 0.4s ease" }}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1.5, justifyContent: "space-between" }}>
            <Legend dot={COLORS.scheduled} label="Scheduled" value={scheduled} />
            <Legend dot={COLORS.sent} label="Sent" value={sent} />
            <Legend dot={COLORS.failed} label="Failed" value={failed} />
            {other > 0 && <Legend dot={COLORS.other} label="Other" value={other} />}
          </Box>
        </>
      )}

      {(recipientFailures > 0 || (s.delivered_total ?? 0) > 0) && (
        <Typography variant="body2" sx={{ mt: 2, pt: 2, borderTop: `1px solid ${dash.border}`, color: dash.textMuted, fontSize: 13 }}>
          Delivery totals (all campaigns in range):{" "}
          <strong style={{ color: "inherit" }}>{(s.delivered_total ?? 0).toLocaleString()}</strong> delivered
          {recipientFailures > 0 && (
            <>
              {" · "}
              <strong style={{ color: "#b91c1c" }}>{recipientFailures.toLocaleString()}</strong> recipient failures
            </>
          )}
        </Typography>
      )}
    </Paper>
  );
}

function Legend({ dot, label, value }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dot, flexShrink: 0 }} />
      <Typography variant="caption" color="text.secondary">
        {label} <strong>{value}</strong>
      </Typography>
    </Box>
  );
}
